import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  LoginResponse,
  TeleVisitQueueItem,
  PatientDetails,
  CaseNoteHistory,
  DispenseHistory,
  Item,
  Diagnosis,
  SaveDispenseRequest,
  HssSelection,
  HssAssessment,
  ChasStatus,
  SaveDispenseResponse,
  SaveDispenseItem,
  SaveSignatureRequest,
  SignatureResponse,
  VisitOrderItem,
  CreateMCRequest,
  CreateMCResponse,
  UpdateMCRequest,
  UpdateMCResponse,
  DeleteMCResponse,
  DrugAllergyItem,
  DrugAllergyDetail,
  SaveDrugAllergyRequest,
  DrugAllergyValidation,
  CodeSetItem,
  TeleVisitMeetStatus,
  VisitMemo,
  SaveMemoRequest,
  SaveMemoResponse,
  DeleteMemoResponse,
  ValidateTokenResponse,
  GenerateConsultLinkResponse,
  StoreListItem,
  TeleStaffChatGetResponse,
  TeleStaffChatMessage,
  TeleChatAppendResult,
  TeleNormalSupportQueueItem,
  TelePaymentFollowUpItem,
  TeleconsultStripeCheckoutSessionResponse,
  TeleconsultPortalConfig,
  PatientIdDocument,
  PatientIdDocumentReview,
  PatientIdDocumentVetRequest,
  TeleconsultManagementCalendarDay,
  TeleconsultManagementDashboard,
  TeleconsultManagementVisit,
  User,
} from '../types/api';
import { serializeStaffChatMessage } from '../features/staff-teleconsult/staffChatMessages';
import { clearPersistedTeleSession } from '../utils/teleSession';
import { APP_BASENAME } from '../appPaths';
import { API_BASE_URL } from '../config/apiBaseUrl';

// Helper function to decode JWT token (for debugging)
function decodeJWT(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (e) {
    console.error('Error decoding JWT:', e);
    return null;
  }
}

function persistAuthSession(data: LoginResponse, loginUsername?: string): LoginResponse {
  if (!data.accessToken) return data;
  const user: User = {
    ...data.user,
    username: data.user.username || loginUsername || '',
  };
  localStorage.setItem('accessToken', data.accessToken);
  localStorage.setItem('user', JSON.stringify(user));
  return { ...data, user };
}

export type TeleconsultStaffCancelPreview = {
  canCancel: boolean;
  blockedReason?: string | null;
  hasDeposit: boolean;
  depositPaidSgd: number;
  alreadyRefundedSgd: number;
  remainingSgd: number;
  defaultAdminFeeSgd: number;
};

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          // Ensure Authorization header is set correctly
          config.headers.Authorization = `Bearer ${token}`;
          
          // Decode and log token info for debugging
          const decoded = decodeJWT(token);
          if (decoded) {
            console.log('=== Request Interceptor ===');
            console.log('URL:', config.url);
            console.log('Method:', config.method);
            console.log('Authorization header set:', !!config.headers.Authorization);
            console.log('Token sub claim:', decoded.sub);
            console.log('Token role_id claim:', decoded.role_id);
            console.log('Token expires:', new Date(decoded.exp * 1000));
            console.log('Token expired?', new Date(decoded.exp * 1000) < new Date());
            console.log('Full Authorization header:', config.headers.Authorization?.substring(0, 50) + '...');
          }
        } else {
          console.warn('No token found in localStorage for request:', config.url);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Only redirect to login for 401 errors, but NOT for:
        // 1. Login endpoint (let LoginPage handle the error)
        // 2. Signature endpoint (let component handle it)
        if (error.response?.status === 401) {
          const requestUrl = error.config?.url || '';
          // Don't auto-redirect for login or signature endpoints - let components handle errors
          if (
            !requestUrl.includes('/api/Auth/doctor/singpass-login') &&
            !requestUrl.includes('/api/Auth/sso') &&
            !requestUrl.includes('/api/Signature')
          ) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('user');
            clearPersistedTeleSession();
            // Use base path for redirect
            window.location.href = `${APP_BASENAME}/login`;
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /** Issue a new staff/doctor token from the current one. Does not replace the stored user profile. */
  async refreshAccessToken(): Promise<void> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No session');
    }
    const response: AxiosResponse<LoginResponse> = await this.api.post('/api/Auth/refresh', { token });
    const next = response.data?.accessToken;
    if (!next) {
      throw new Error('No token');
    }
    localStorage.setItem('accessToken', next);
  }

  async completeHqSso(request: {
    code: string;
    codeVerifier: string;
    redirectUri: string;
    clientId: string;
  }): Promise<{ data: LoginResponse; portal: 'doctor' | 'staff' }> {
    const response: AxiosResponse<LoginResponse> = await this.api.post('/api/Auth/sso', request);
    const data = persistAuthSession(response.data);
    const portal = data.user.roleId === 2 ? 'doctor' : 'staff';
    return { data, portal };
  }

  async getDoctorSingpassAuthUrl(): Promise<string> {
    const response: AxiosResponse<{ authUrl: string }> = await this.api.get('/api/Auth/doctor/singpass-auth-url');
    const url = response.data?.authUrl;
    if (!url) {
      throw new Error('Singpass auth URL was empty');
    }
    return url;
  }

  async singpassDoctorLogin(state: string): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await this.api.post('/api/Auth/doctor/singpass-login', {
      state,
    });
    return persistAuthSession(response.data);
  }

  // Stores
  async getStores(): Promise<StoreListItem[]> {
    const response: AxiosResponse<StoreListItem[]> = await this.api.get('/api/TeleVisit/stores');
    return response.data;
  }

  async getTeleconsultStaffChat(visitUlid: string): Promise<TeleStaffChatGetResponse> {
    const response: AxiosResponse<TeleStaffChatGetResponse> = await this.api.get(
      '/api/TeleVisit/teleconsult-staff-chat',
      {
        params: { visitUlid },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      }
    );
    return response.data;
  }

  async saveTeleconsultStaffChat(
    visitUlid: string,
    messages: TeleStaffChatMessage[],
    inchargeEmpId?: number | null
  ): Promise<void> {
    const chatConversation = JSON.stringify(messages.map((m) => serializeStaffChatMessage(m)));
    await this.api.post('/api/TeleVisit/teleconsult-staff-chat', {
      visitUlid,
      chatConversation,
      inchargeEmpId: inchargeEmpId ?? null,
    });
  }

  /** Append ONE staff message (server merges under a row lock; idempotent by clientMsgId). */
  async appendTeleconsultStaffChatMessage(
    visitUlid: string,
    message: string,
    clientMsgId: string
  ): Promise<TeleChatAppendResult> {
    const response: AxiosResponse<TeleChatAppendResult> = await this.api.post(
      '/api/TeleVisit/teleconsult-staff-chat/message',
      { visitUlid, message, clientMsgId }
    );
    return response.data;
  }

  /** Append ONE staff message to a general / corporate Live Support chat. */
  async appendTeleconsultNormalSupportChatMessage(
    chatUlid: string,
    message: string,
    clientMsgId: string
  ): Promise<TeleChatAppendResult> {
    const response: AxiosResponse<TeleChatAppendResult> = await this.api.post(
      '/api/TeleVisit/teleconsult-normal-support-chat/message',
      { chatUlid, message, clientMsgId }
    );
    return response.data;
  }

  /** Mark a chat as "Waiting for partner / customer" (they can still reply). */
  async markTeleconsultChatPendingPartner(target: { visitUlid?: string; chatUlid?: string }): Promise<void> {
    await this.api.post('/api/TeleVisit/teleconsult-chat-pending', target);
  }

  async createTeleconsultStripeCheckoutSession(
    visitUlid: string,
    amount: number
  ): Promise<TeleconsultStripeCheckoutSessionResponse> {
    const response: AxiosResponse<TeleconsultStripeCheckoutSessionResponse> = await this.api.post(
      '/api/TeleVisit/teleconsult-stripe-checkout-session',
      { visitUlid, amount }
    );
    return response.data;
  }

  async getTeleconsultPortalConfig(): Promise<TeleconsultPortalConfig> {
    const response: AxiosResponse<TeleconsultPortalConfig> = await this.api.get(
      '/api/TeleVisit/teleconsult-portal-config',
      { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } }
    );
    return response.data;
  }

  async claimTeleconsultChatForVisit(visitUlid: string): Promise<void> {
    await this.api.post('/api/TeleVisit/teleconsult-chat-claim', { visitUlid });
  }

  async releaseTeleconsultChatForVisit(visitUlid: string): Promise<void> {
    await this.api.post('/api/TeleVisit/teleconsult-chat-release', { visitUlid });
  }

  async closeTeleconsultChatForVisit(visitUlid: string): Promise<void> {
    await this.api.post('/api/TeleVisit/teleconsult-chat-close', { visitUlid });
  }

  async getTeleconsultNormalSupportQueue(): Promise<TeleNormalSupportQueueItem[]> {
    const response: AxiosResponse<TeleNormalSupportQueueItem[]> = await this.api.get(
      '/api/TeleVisit/teleconsult-normal-support-queue',
      { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } }
    );
    return response.data;
  }

  async getTeleconsultPaymentFollowUp(): Promise<TelePaymentFollowUpItem[]> {
    const response: AxiosResponse<TelePaymentFollowUpItem[]> = await this.api.get(
      '/api/TeleVisit/teleconsult-payment-follow-up',
      { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } }
    );
    return response.data;
  }

  async startTeleconsultPaymentFollowUpChat(visitUlid: string): Promise<TeleNormalSupportQueueItem> {
    const response: AxiosResponse<TeleNormalSupportQueueItem> = await this.api.post(
      '/api/TeleVisit/teleconsult-payment-follow-up/start-chat',
      { visitUlid }
    );
    return response.data;
  }

  async getTeleconsultNormalSupportChat(chatUlid: string): Promise<TeleStaffChatGetResponse> {
    const response: AxiosResponse<TeleStaffChatGetResponse> = await this.api.get(
      '/api/TeleVisit/teleconsult-normal-support-chat',
      {
        params: { chatUlid },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      }
    );
    return response.data;
  }

  async saveTeleconsultNormalSupportChat(
    chatUlid: string,
    messages: TeleStaffChatMessage[],
    inchargeEmpId?: number | null
  ): Promise<void> {
    const chatConversation = JSON.stringify(messages.map((m) => serializeStaffChatMessage(m)));
    await this.api.post('/api/TeleVisit/teleconsult-normal-support-chat', {
      chatUlid,
      chatConversation,
      inchargeEmpId: inchargeEmpId ?? null,
    });
  }

  async claimTeleconsultNormalSupportChat(chatUlid: string): Promise<void> {
    await this.api.post('/api/TeleVisit/teleconsult-normal-support-chat-claim', { chatUlid });
  }

  async releaseTeleconsultNormalSupportChat(chatUlid: string): Promise<void> {
    await this.api.post('/api/TeleVisit/teleconsult-normal-support-chat-release', { chatUlid });
  }

  async closeTeleconsultNormalSupportChat(chatUlid: string): Promise<void> {
    await this.api.post('/api/TeleVisit/teleconsult-normal-support-chat-close', { chatUlid });
  }

  // TeleVisit
  async getTeleVisitQueue(
    visitDate?: string,
    storeId?: number,
    scope?: 'available' | 'mine' | 'all'
  ): Promise<TeleVisitQueueItem[]> {
    const params: Record<string, string | number> = {};
    if (visitDate) params.visitDate = visitDate;
    if (storeId != null && !Number.isNaN(storeId)) params.storeId = storeId;
    if (scope) params.scope = scope;
    // Avoid stale / shared GET cache between parallel scope=available vs scope=mine (proxies, browsers).
    params._ = Date.now();

    const response: AxiosResponse<TeleVisitQueueItem[]> = await this.api.get('/api/TeleVisit/queue', {
      params,
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    return response.data;
  }

  async getPatientDetails(visitULID: string): Promise<PatientDetails> {
    const response: AxiosResponse<PatientDetails> = await this.api.get(`/api/TeleVisit/${visitULID}`);
    return response.data;
  }

  /** Patient ID attachment(s) for staff verification (404 if none). */
  async getPatientIdDocumentReview(visitULID: string): Promise<PatientIdDocumentReview | null> {
    try {
      const response: AxiosResponse<PatientIdDocumentReview> = await this.api.get(
        `/api/TeleVisit/${visitULID}/patient-id-document`,
      );
      return response.data;
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }

  async getPatientIdDocumentContent(visitULID: string, attachmentUlid?: string): Promise<Blob> {
    const params: Record<string, string> = {};
    if (attachmentUlid) {
      params.attachmentUlid = attachmentUlid;
    }
    const response = await this.api.get(`/api/TeleVisit/${visitULID}/patient-id-document/content`, {
      responseType: 'blob',
      params,
    });
    return response.data;
  }

  async vetPatientIdDocument(
    visitULID: string,
    body: PatientIdDocumentVetRequest,
  ): Promise<PatientIdDocument> {
    const response: AxiosResponse<PatientIdDocument> = await this.api.post(
      `/api/TeleVisit/${visitULID}/patient-id-document/vet`,
      body,
    );
    return response.data;
  }

  async uploadPatientIdDocumentOnBehalf(
    visitULID: string,
    patientIdDocument: File,
    patientIdDocumentBack?: File,
  ): Promise<void> {
    const form = new FormData();
    form.append('patientIdDocument', patientIdDocument, patientIdDocument.name);
    if (patientIdDocumentBack) {
      form.append('patientIdDocumentBack', patientIdDocumentBack, patientIdDocumentBack.name);
    }
    await this.api.post(`/api/TeleVisit/${visitULID}/patient-id-document/upload`, form);
  }

  async getTeleconsultStaffChatImage(visitULID: string, imageUlid: string): Promise<Blob> {
    const response = await this.api.get(`/api/TeleVisit/${visitULID}/teleconsult-staff-chat/image`, {
      params: { imageUlid },
      responseType: 'blob',
    });
    return response.data;
  }

  async getTeleconsultNormalSupportChatImage(chatUlid: string, imageUlid: string): Promise<Blob> {
    const response = await this.api.get('/api/TeleVisit/teleconsult-normal-support-chat/image', {
      params: { chatUlid, imageUlid },
      responseType: 'blob',
    });
    return response.data;
  }

  async saveTeleconsultStaffChatImageAsPatientId(visitULID: string, imageUlid: string): Promise<void> {
    await this.api.post(`/api/TeleVisit/${visitULID}/teleconsult-staff-chat/image/save-as-patient-id`, {
      imageUlid,
    });
  }

  /** Live Meet status (Meet API v2): active conference + in-call participant count when available. */
  async getTeleVisitMeetStatus(visitULID: string): Promise<TeleVisitMeetStatus> {
    const response: AxiosResponse<TeleVisitMeetStatus> = await this.api.get(
      `/api/TeleVisit/${visitULID}/meet-status`,
      { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } }
    );
    return response.data;
  }

  async getCaseNoteHistory(visitULID: string): Promise<CaseNoteHistory[]> {
    const response: AxiosResponse<CaseNoteHistory[]> = await this.api.get(`/api/TeleVisit/${visitULID}/case-notes`);
    return response.data;
  }

  async getDispenseHistory(visitULID: string): Promise<DispenseHistory[]> {
    const response: AxiosResponse<DispenseHistory[]> = await this.api.get(`/api/TeleVisit/${visitULID}/dispense-history`);
    return response.data;
  }

  async getCurrentOrders(visitULID: string): Promise<VisitOrderItem[]> {
    const response: AxiosResponse<VisitOrderItem[]> = await this.api.get(`/api/TeleVisit/${visitULID}/orders`);
    return response.data;
  }

  async lockVisit(visitULID: string): Promise<boolean> {
    const response: AxiosResponse<boolean> = await this.api.post(`/api/TeleVisit/${visitULID}/lock`);
    return response.data;
  }

  async unlockVisit(visitULID: string): Promise<boolean> {
    const response: AxiosResponse<boolean> = await this.api.post(`/api/TeleVisit/${visitULID}/unlock`);
    return response.data;
  }

  async getVisitLockStatus(visitULID: string): Promise<boolean> {
    const response: AxiosResponse<boolean> = await this.api.get(`/api/TeleVisit/${visitULID}/lock-status`);
    return response.data;
  }

  async pickVisit(visitULID: string): Promise<boolean> {
    const response: AxiosResponse<boolean> = await this.api.post(`/api/TeleVisit/${visitULID}/pick`);
    return response.data;
  }

  async unpickVisit(visitULID: string): Promise<boolean> {
    const response: AxiosResponse<boolean> = await this.api.post(`/api/TeleVisit/${visitULID}/unpick`);
    return response.data;
  }

  /** Staff: return a picked tele visit to the doctor queue (EmpId cleared); caller need not be the picking doctor. */
  async staffUnclaimTeleVisit(visitULID: string): Promise<boolean> {
    const response: AxiosResponse<boolean> = await this.api.post(`/api/TeleVisit/${visitULID}/staff-unclaim`);
    return response.data;
  }

  async createMC(visitULID: string, request: CreateMCRequest): Promise<CreateMCResponse> {
    const response: AxiosResponse<CreateMCResponse> = await this.api.post(`/api/TeleVisit/${visitULID}/mc`, request);
    return response.data;
  }

  async updateMC(visitULID: string, mcULID: string, request: UpdateMCRequest): Promise<UpdateMCResponse> {
    const response: AxiosResponse<UpdateMCResponse> = await this.api.put(`/api/TeleVisit/${visitULID}/mc/${mcULID}`, request);
    return response.data;
  }

  async deleteMC(visitULID: string, mcULID: string): Promise<DeleteMCResponse> {
    const response: AxiosResponse<DeleteMCResponse> = await this.api.delete(`/api/TeleVisit/${visitULID}/mc/${mcULID}`);
    return response.data;
  }

  async saveDispense(visitULID: string, request: SaveDispenseRequest): Promise<SaveDispenseResponse> {
    const response: AxiosResponse<SaveDispenseResponse> = await this.api.post(
      `/api/TeleVisit/${visitULID}/save`,
      request
    );
    return response.data;
  }

  async finishConsult(visitULID: string, hss?: HssSelection | null): Promise<{
    success: boolean;
    message: string;
    alreadyFinalized?: boolean;
    hss?: HssAssessment | null;
  }> {
    const response = await this.api.post(
      `/api/TeleVisit/${visitULID}/finish-consult`,
      hss ? { hss } : undefined,
    );
    return response.data;
  }

  /** HSS panel: eligibility, per-condition 14-day tele limit, Table 2 preview (read-only). */
  async getHssAssessment(
    visitULID: string,
    conditions?: string[] | null,
    diagnosisCodes?: string[] | null,
  ): Promise<HssAssessment> {
    const params: Record<string, string> = {};
    if (conditions) params.conditions = conditions.join(',');
    if (diagnosisCodes) params.diagnoses = diagnosisCodes.join(',');
    const response: AxiosResponse<HssAssessment> = await this.api.get(`/api/TeleVisit/${visitULID}/hss`, { params });
    return response.data;
  }

  /** Queue a background Nemo CHAS refresh; returns the current badge. */
  async refreshChas(visitULID: string): Promise<ChasStatus> {
    const response: AxiosResponse<ChasStatus> = await this.api.post(`/api/TeleVisit/${visitULID}/chas/refresh`);
    return response.data;
  }

  // Items
  // Note: Backend should filter items based on lg.item_activestatus table:
  // - Only return items where inactive = 0 (not inactive)
  // - Only return items where discontinued = 0 (not discontinued)
  // - Filter by storeId from the visit (storeID in item_activestatus table)
  // This matches CheckLine app behavior
  async searchItems(searchTerm: string, itemType: string = 'ALL', storeId: number): Promise<Item[]> {
    const response: AxiosResponse<Item[]> = await this.api.get('/api/Item/search', {
      params: { 
        search: searchTerm, 
        type: itemType, 
        storeId,
        // Backend should use these filters:
        // - WHERE inactive = 0 AND discontinued = 0
        // - WHERE storeID = storeId (from lg.item_activestatus table)
      }
    });
    return response.data;
  }

  // Diagnosis
  async getAcuteDiagnoses(): Promise<Diagnosis[]> {
    const response: AxiosResponse<Diagnosis[]> = await this.api.get('/api/Diagnosis/acute');
    return response.data;
  }

  async getChronicDiagnoses(): Promise<Diagnosis[]> {
    const response: AxiosResponse<Diagnosis[]> = await this.api.get('/api/Diagnosis/chronic');
    return response.data;
  }

  // Signature
  async saveSignature(request: SaveSignatureRequest): Promise<SignatureResponse> {
    // Send base64 string directly - backend will convert to byte array
    const token = localStorage.getItem('accessToken');
    console.log('saveSignature - Token exists:', !!token);
    if (token) {
      console.log('saveSignature - Token preview:', token.substring(0, 20) + '...');
    }
    
    const response: AxiosResponse<SignatureResponse> = await this.api.post('/api/Signature', {
      empID: request.empID,
      multiUse: request.multiUse,
      signatureDataBase64: request.signatureData
    });
    return response.data;
  }

  async getSignature(empID: number): Promise<SignatureResponse> {
    const response: AxiosResponse<SignatureResponse> = await this.api.get(`/api/Signature/${empID}`);
    return response.data;
  }

  getMCHistory(visitULID: string): Promise<any[]> {
    return this.api.get<any[]>(`/api/TeleVisit/${visitULID}/mc-history`).then(res => res.data);
  }

  getCurrentCaseNote(visitULID: string): Promise<{ caseNote: string | null }> {
    return this.api.get<{ caseNote: string | null }>(`/api/TeleVisit/${visitULID}/case-note`).then(res => res.data);
  }

  // Memo API methods
  async getMemos(visitULID: string): Promise<VisitMemo[]> {
    const response: AxiosResponse<VisitMemo[]> = await this.api.get(`/api/TeleVisit/${visitULID}/memos`);
    return response.data;
  }

  async getMemosByCustomer(customerULID: string): Promise<VisitMemo[]> {
    const response: AxiosResponse<VisitMemo[]> = await this.api.get(`/api/TeleVisit/customers/${customerULID}/memos`);
    return response.data;
  }

  async saveMemo(visitULID: string, request: SaveMemoRequest): Promise<SaveMemoResponse> {
    const response: AxiosResponse<SaveMemoResponse> = await this.api.post(
      `/api/TeleVisit/${visitULID}/memos`,
      request
    );
    return response.data;
  }

  async deleteMemo(visitULID: string, memoULID: string): Promise<DeleteMemoResponse> {
    const response: AxiosResponse<DeleteMemoResponse> = await this.api.delete(
      `/api/TeleVisit/${visitULID}/memos/${memoULID}`
    );
    return response.data;
  }

  // Add a single item to VisitItemEntry immediately (matching CheckLine's AddOrder behavior)
  async addOrderItem(visitULID: string, item: SaveDispenseItem): Promise<VisitOrderItem> {
    const response: AxiosResponse<VisitOrderItem> = await this.api.post(
      `/api/TeleVisit/${visitULID}/orders`,
      item
    );
    return response.data;
  }

  // Remove (void) a single item from VisitItemEntry
  async removeOrderItem(visitULID: string, itemID: number): Promise<boolean> {
    try {
      const response: AxiosResponse<boolean> = await this.api.delete(
        `/api/TeleVisit/${visitULID}/orders/${itemID}`
      );
      // Backend returns true on success, false or error on failure
      return response.data === true;
    } catch (error: any) {
      console.error('Error removing order item:', error);
      // If it's a 409 Conflict (item locked or not found), return false
      if (error.response?.status === 409) {
        return false;
      }
      // For other errors, throw to be handled by caller
      throw error;
    }
  }

  // Save case note for a visit
  async saveCaseNote(visitULID: string, caseNote: string): Promise<SaveDispenseResponse> {
    const response = await this.api.post<SaveDispenseResponse>(
      `/api/TeleVisit/${visitULID}/case-note`,
      {
        visitULID: visitULID,
        caseNote: caseNote
      }
    );
    return response.data;
  }

  // Drug Allergy management (matching CheckLine's FormDrugAllergies)
  async getDrugList(searchTerm?: string): Promise<DrugAllergyItem[]> {
    const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
    const response = await this.api.get<DrugAllergyItem[]>(`/api/TeleVisit/drugs${params}`);
    return response.data;
  }

  async getDrugAllergyDetails(customerULID: string): Promise<DrugAllergyDetail[]> {
    const response = await this.api.get<DrugAllergyDetail[]>(`/api/TeleVisit/customers/${customerULID}/drug-allergies`);
    return response.data;
  }

  async saveDrugAllergy(customerULID: string, request: SaveDrugAllergyRequest): Promise<SaveDispenseResponse> {
    const response = await this.api.post<SaveDispenseResponse>(
      `/api/TeleVisit/customers/${customerULID}/drug-allergies`,
      request
    );
    return response.data;
  }

  async saveNoDrugAllergy(customerULID: string): Promise<SaveDispenseResponse> {
    const response = await this.api.post<SaveDispenseResponse>(
      `/api/TeleVisit/customers/${customerULID}/no-drug-allergy`
    );
    return response.data;
  }

  async removeDrugAllergy(customerULID: string, drugCode: string): Promise<SaveDispenseResponse> {
    const response = await this.api.delete<SaveDispenseResponse>(
      `/api/TeleVisit/customers/${customerULID}/drug-allergies/${encodeURIComponent(drugCode)}`
    );
    return response.data;
  }

  async getDrugAllergyValidation(customerULID: string): Promise<DrugAllergyValidation> {
    const response = await this.api.get<DrugAllergyValidation>(
      `/api/TeleVisit/customers/${customerULID}/drug-allergy-validation`
    );
    return response.data;
  }

  // Code Set methods (matching CheckLine's code sets)
  async getDrugProbabilityList(): Promise<CodeSetItem[]> {
    const response = await this.api.get<CodeSetItem[]>(`/api/TeleVisit/code-sets/drug-probability`);
    return response.data;
  }

  async getInformationSourceList(): Promise<CodeSetItem[]> {
    const response = await this.api.get<CodeSetItem[]>(`/api/TeleVisit/code-sets/information-source`);
    return response.data;
  }

  async getDrugAllergyIndicatorList(): Promise<CodeSetItem[]> {
    const response = await this.api.get<CodeSetItem[]>(`/api/TeleVisit/code-sets/drug-allergy-indicator`);
    return response.data;
  }

  async getAlertIndicatorList(): Promise<CodeSetItem[]> {
    const response = await this.api.get<CodeSetItem[]>(`/api/TeleVisit/code-sets/alert-indicator`);
    return response.data;
  }

  async getReactionOutcomeList(): Promise<CodeSetItem[]> {
    const response = await this.api.get<CodeSetItem[]>(`/api/TeleVisit/code-sets/reaction-outcome`);
    return response.data;
  }

  async getAdverseReactionList(): Promise<CodeSetItem[]> {
    const response = await this.api.get<CodeSetItem[]>(`/api/TeleVisit/code-sets/adverse-reaction`);
    return response.data;
  }

  // Visit Timestamp logging (matching CheckLine's Visit.TimeStamp behavior)
  // Steps: PickCustomer=30, ConsultStart=31, ConsultEnd=35, GivenMed=40, CheckOut=60
  // Note: PickCustomer+ConsultStart are auto-logged on pick, ConsultEnd on save.
  async logTimestamp(visitULID: string, step: number, machineName?: string): Promise<void> {
    try {
      await this.api.post(`/api/TeleVisit/${visitULID}/timestamp`, {
        step,
        machineName: machineName || 'DoctorConsultWeb',
      });
    } catch (error) {
      // Non-critical: log but don't fail
      console.error('Failed to log visit timestamp:', error);
    }
  }

  async getTeleconsultManagementDashboard(params: {
    from: string;
    to: string;
    type?: string;
    status?: string;
  }): Promise<TeleconsultManagementDashboard> {
    const response = await this.api.get<TeleconsultManagementDashboard>('/api/TeleVisit/management/dashboard', {
      params: {
        from: params.from,
        to: params.to,
        type: params.type && params.type !== 'all' ? params.type : undefined,
        status: params.status && params.status !== 'all' ? params.status : undefined,
      },
    });
    return response.data;
  }

  async getTeleconsultManagementVisits(params: {
    from: string;
    to: string;
    type?: string;
    status?: string;
  }): Promise<TeleconsultManagementVisit[]> {
    const response = await this.api.get<TeleconsultManagementVisit[]>('/api/TeleVisit/management/visits', {
      params: {
        from: params.from,
        to: params.to,
        type: params.type && params.type !== 'all' ? params.type : undefined,
        status: params.status && params.status !== 'all' ? params.status : undefined,
      },
    });
    return response.data;
  }

  async getTeleconsultManagementCalendar(params: {
    from: string;
    to: string;
  }): Promise<TeleconsultManagementCalendarDay[]> {
    const response = await this.api.get<TeleconsultManagementCalendarDay[]>('/api/TeleVisit/management/calendar', {
      params: { from: params.from, to: params.to },
    });
    return response.data;
  }

  async getTeleconsultManagementAccess(): Promise<{ allowed: boolean }> {
    const response = await this.api.get<{ allowed: boolean }>('/api/TeleVisit/management/access');
    return response.data;
  }

  async cancelTeleconsultManagementVisit(
    visitUlid: string,
    reasonCode: string,
    reasonOther?: string,
  ): Promise<void> {
    await this.api.post(`/api/TeleVisit/management/${visitUlid}/cancel`, {
      reasonCode,
      reasonOther: reasonOther || undefined,
    });
  }

  async refundTeleconsultManagementVisit(
    visitUlid: string,
    reason: string,
    mode: 'full' | 'retain2' | 'custom' = 'full',
    customAmountSgd?: number,
  ): Promise<void> {
    await this.api.post(`/api/TeleVisit/management/${visitUlid}/refund`, {
      reason,
      mode,
      customAmountSgd: mode === 'custom' ? customAmountSgd : undefined,
    });
  }

  async getTeleconsultStaffCancelPreview(visitUlid: string): Promise<TeleconsultStaffCancelPreview> {
    const response = await this.api.get<TeleconsultStaffCancelPreview>(
      `/api/TeleVisit/${visitUlid}/staff-cancel-preview`,
    );
    return response.data;
  }

  async cancelTeleconsultFromStaffQueue(
    visitUlid: string,
    body: { reasonCode: string; reasonOther?: string; adminFeeSgd?: number },
  ): Promise<void> {
    await this.api.post(`/api/TeleVisit/${visitUlid}/staff-cancel`, body);
  }

  // External Consult
  async validateConsultToken(token: string): Promise<ValidateTokenResponse> {
    const response = await this.api.get<ValidateTokenResponse>(
      `/api/ExternalConsult/validate-token`,
      { params: { token } }
    );
    return response.data;
  }

  /** Ensures a tele consult row + Google Meet for this visit (reuses existing Meet per visit on the server). */
  async generateConsultLink(visitULID: string): Promise<GenerateConsultLinkResponse> {
    const response = await this.api.post<GenerateConsultLinkResponse>(
      '/api/ExternalConsult/generate-consult-link',
      { visitULID }
    );
    return response.data;
  }
}

export const apiService = new ApiService();


